import React, { useState, useEffect } from 'react';
import type {
  RubricAnalysis,
  RubricCategoryResult,
  RubricEvidence,
  RubricFix,
  RubricStrength,
} from '../adherence/types';
import { formatCallInsightsTimestamp } from '../adherence/types';

/**
 * Renders the Lushin Discovery Call Rubric result (callInsights.rubricAnalysis):
 * a band verdict, the 3-section coach narrative, and a 10-category score sheet
 * laid out as a grid of cards. Clicking a card opens a large modal (80vw × 80vh)
 * with the category's verdict, a collapsible timestamped-evidence view (clickable
 * → jumps to the transcript timeline), and a two-column "what went well / the fix"
 * layout. Light theme to match the admin-portal.
 *
 * The two columns are asymmetric on purpose. A strength happened, so it carries
 * the moment that proves it; a fix did not happen, so it carries the words the
 * rep could have used — taken from the Lushin playbook where a line fit.
 *
 * Every list here degrades to a paragraph: calls graded before the analyzer
 * emitted bullets have only `justification` / `what_went_well` /
 * `what_was_missed`, and a partial re-score (an admin reweight) can leave one
 * dimension in each shape inside the same call.
 */

interface Props {
  rubricAnalysis?: RubricAnalysis;
  onNavigateToTimestamp?: (iso: string) => void;
  // For the incomplete-analysis error banner: admins can re-run, everyone can
  // reach support. Supplied by CallInsightsTab (which owns the re-run action
  // and the support-ticket modal).
  isAdmin?: boolean;
  onReanalyze?: () => void;
  isReanalyzing?: boolean;
  onContactSupport?: () => void;
  // True for a historical upload with two salespeople (team-selling call) —
  // the score above is one combined composite, not two separate scorecards.
  multipleSalespeople?: boolean;
  // Opens the per-call weight editor. Admin-only; omitted for everyone else.
  onAdjustWeights?: () => void;
}

/**
 * Shared classes built on the Lushin design tokens (styles/lushin-tokens.css,
 * surfaced through tailwind.config.js): obsidian for primary ink and fills,
 * plasma-cyan strictly as an accent — at #00E5C7 it fails contrast as text on
 * white — bone as the on-obsidian text colour, and dim for secondary copy.
 * Surfaces stay white/gray so the accent has something quiet to sit against.
 */
const BTN_PRIMARY =
  'inline-flex items-center rounded px-3.5 py-1.5 text-xs font-semibold tracking-wide ' +
  'bg-obsidian text-bone hover:bg-void transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-plasma-cyan focus-visible:ring-offset-1 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';

const BTN_SECONDARY =
  'inline-flex items-center rounded px-3.5 py-1.5 text-xs font-semibold tracking-wide ' +
  'bg-white text-obsidian border border-gray-300 hover:bg-gray-50 transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-plasma-cyan focus-visible:ring-offset-1 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';

/** Card/section heading — Space Grotesk, the token display face. */
const SECTION_LABEL =
  'font-display text-[11px] font-bold uppercase tracking-[0.14em] text-obsidian';

/** Heading for a block inside a card. */
const SUB_LABEL = 'font-display text-xs font-semibold text-obsidian';

const BODY_TEXT = 'text-sm text-gray-700 leading-relaxed mt-1';

const BAND_STYLE: Record<string, { text: string; bg: string; border: string }> = {
  standard: { text: 'text-teal-700', bg: 'bg-teal-50', border: 'border-l-teal-500' },
  strong: { text: 'text-green-700', bg: 'bg-green-50', border: 'border-l-green-500' },
  soft: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-l-amber-500' },
  at_risk: { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-l-orange-500' },
  lost: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-l-red-500' },
};

// Per-score visual treatment: chip (score pill) + accent (card left bar) + ring.
const scoreStyle = (
  score?: number | null,
): { chip: string; accent: string } => {
  if (score == null) return { chip: 'bg-gray-100 text-gray-500', accent: 'bg-gray-300' };
  if (score >= 5) return { chip: 'bg-teal-100 text-teal-800', accent: 'bg-teal-500' };
  if (score >= 4) return { chip: 'bg-green-100 text-green-800', accent: 'bg-green-500' };
  if (score >= 3) return { chip: 'bg-amber-100 text-amber-800', accent: 'bg-amber-500' };
  if (score >= 2) return { chip: 'bg-orange-100 text-orange-800', accent: 'bg-orange-500' };
  return { chip: 'bg-red-100 text-red-800', accent: 'bg-red-500' };
};

const pad2 = (n?: number, fallback = 0) => String(n ?? fallback).padStart(2, '0');

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <span
    className="text-[10px] text-gray-400 transition-transform duration-200 flex-shrink-0"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
  >
    &#9660;
  </span>
);

/** One clickable timestamped evidence quote. */
const EvidenceItem: React.FC<{
  ev: RubricEvidence;
  onNavigateToTimestamp?: (iso: string) => void;
}> = ({ ev, onNavigateToTimestamp }) => (
  <div className="border-l-2 border-gray-300 bg-gray-50 rounded-r pl-3 pr-2 py-2">
    <p className="text-sm text-gray-700 italic">&ldquo;{ev.quote}&rdquo;</p>
    {(ev.timestamp_iso || ev.timestamp) && (
      <button
        type="button"
        onClick={() => ev.timestamp_iso && onNavigateToTimestamp?.(ev.timestamp_iso)}
        disabled={!ev.timestamp_iso}
        className="mt-1 inline-flex items-center gap-1 text-xs font-mono text-gray-600 hover:text-obsidian hover:underline decoration-plasma-cyan decoration-2 underline-offset-2 disabled:text-gray-400 disabled:no-underline disabled:cursor-default"
        title={ev.timestamp_iso ? 'Jump to this moment in the timeline' : undefined}
      >
        <span aria-hidden>&#9654;</span> {ev.timestamp || ev.timestamp_iso}
      </button>
    )}
  </div>
);

/** Bullet marker shared by every list in the modal, so they scan as one system. */
const Bullet: React.FC<{ className?: string }> = ({ className = 'bg-gray-400' }) => (
  <span
    className={`mt-[0.45rem] h-1.5 w-1.5 flex-shrink-0 rounded-full ${className}`}
    aria-hidden
  />
);

/**
 * The verdict: bullets when the analyzer produced them, the original paragraph
 * when it didn't. Callers guard on there being one or the other.
 */
const PointsOrProse: React.FC<{ points?: string[]; prose?: string }> = ({ points, prose }) => {
  const list = (points ?? []).filter(Boolean);
  if (list.length === 0) {
    return <p className="text-sm text-gray-700 leading-relaxed">{prose}</p>;
  }
  return (
    <ul className="space-y-1.5">
      {list.map((point, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
          <Bullet />
          <span>{point}</span>
        </li>
      ))}
    </ul>
  );
};

/** One "what went well" bullet with the moment that proves it. */
const StrengthItem: React.FC<{
  strength: RubricStrength;
  onNavigateToTimestamp?: (iso: string) => void;
}> = ({ strength, onNavigateToTimestamp }) => (
  <li className="space-y-1.5">
    <div className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
      <Bullet className="bg-green-500" />
      <span>{strength?.point}</span>
    </div>
    {strength?.evidence?.quote && (
      <div className="pl-[1.4rem]">
        <EvidenceItem ev={strength.evidence} onNavigateToTimestamp={onNavigateToTimestamp} />
      </div>
    )}
  </li>
);

/** One "the fix" bullet with the words the rep could have said instead. */
const FixItem: React.FC<{ fix: RubricFix }> = ({ fix }) => (
  <li className="space-y-1.5">
    <div className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
      <Bullet className="bg-amber-500" />
      <span>{fix?.point}</span>
    </div>
    {fix?.suggested_language && (
      <div className="pl-[1.4rem]">
        <div className="border-l-2 border-amber-400 bg-white rounded-r pl-3 pr-2 py-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
            Suggested language
          </div>
          <p className="text-sm text-gray-800 italic">&ldquo;{fix.suggested_language}&rdquo;</p>
          {fix?.playbook_reference && (
            <div className="mt-1.5 text-[11px] text-dim">
              Playbook: <span className="font-medium text-obsidian">{fix.playbook_reference}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </li>
);

/** Compact card for a single rubric dimension — opens the detail modal on click. */
const CategoryCard: React.FC<{
  cat: RubricCategoryResult;
  onClick: () => void;
  // Only shown when this call was custom-weighted — at equal weights a "1.0×"
  // on every card is noise.
  showWeight?: boolean;
}> = ({ cat, onClick, showWeight }) => {
  const ss = scoreStyle(cat?.raw_score);
  const refs = cat?.evidence?.length ?? 0;
  const ungraded = cat?.raw_score == null || !!cat?.error;
  const weight = cat?.weight;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative text-left bg-white rounded-lg border border-gray-200 shadow-1 hover:shadow-2 hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-150 p-4 pl-5 flex flex-col gap-2.5 overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-plasma-cyan"
    >
      {/* score accent bar */}
      <span className={`absolute inset-y-0 left-0 w-1.5 ${ss.accent}`} aria-hidden />

      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-mono font-semibold text-gray-400">{pad2(cat?.number)}</span>
        <div className="flex items-center gap-1.5">
          {/* Emphasised dimensions get the obsidian fill; de-emphasised ones
              recede into gray. Reads as a hierarchy at a glance. */}
          {showWeight && weight != null && (
            <span
              className={`font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${
                weight > 1
                  ? 'bg-obsidian text-bone'
                  : weight < 1
                  ? 'bg-gray-100 text-dim'
                  : 'bg-gray-100 text-gray-600'
              }`}
              title={`Counts ${weight.toFixed(1)}× toward this call's score`}
            >
              {weight.toFixed(1)}&times;
            </span>
          )}
          <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${ss.chip}`}>
            {cat?.raw_score != null ? `${cat.raw_score}/5` : '—'}
          </span>
        </div>
      </div>

      <div className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2 min-h-[2.5rem]">
        {cat?.title}
      </div>

      <div className="mt-auto flex items-center justify-between text-[11px] text-gray-400">
        <span>
          {ungraded ? 'Not graded' : `${refs} ref${refs === 1 ? '' : 's'}`}
        </span>
        <span className="font-medium text-obsidian opacity-0 group-hover:opacity-100 transition-opacity">
          View details →
        </span>
      </div>
    </button>
  );
};

/** Full-detail modal (80vw × 80vh) for one rubric dimension. */
const CategoryModal: React.FC<{
  cat: RubricCategoryResult;
  onClose: () => void;
  onNavigateToTimestamp?: (iso: string) => void;
}> = ({ cat, onClose, onNavigateToTimestamp }) => {
  const ss = scoreStyle(cat?.raw_score);
  const evidence = cat?.evidence ?? [];
  const strengths = cat?.strengths ?? [];
  const fixes = cat?.fixes ?? [];
  // The section below justifies the SCORE — including moments that show what
  // went wrong, which the strengths column cannot carry. It stays, but once the
  // strengths quote their own moments it opens on a near-duplicate list, so it
  // starts collapsed whenever those per-point quotes exist.
  const hasInlineEvidence = strengths.some((s) => !!s?.evidence?.quote);
  const [evidenceOpen, setEvidenceOpen] = useState(!hasInlineEvidence);

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Clicking a timestamp jumps to the timeline — close the modal so it's visible.
  const jump = (iso: string) => {
    onNavigateToTimestamp?.(iso);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={cat?.title}
    >
      <div
        className="bg-white rounded-lg shadow-3 w-[80vw] h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-4 min-w-0">
            <div
              className={`flex-shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center font-extrabold ${ss.chip}`}
            >
              <span className="text-2xl leading-none">
                {cat?.raw_score != null ? cat.raw_score : '—'}
              </span>
              <span className="text-[10px] font-semibold opacity-70">/ 5</span>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-mono font-semibold text-gray-400">
                Dimension {pad2(cat?.number)}
              </div>
              <h3 className="text-xl font-bold text-gray-800 leading-tight">{cat?.title}</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {cat?.error || cat?.raw_score == null ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
              <p className="text-gray-500 text-sm">
                This dimension couldn&apos;t be graded automatically for this call. Try re-running
                the analysis from the Call Insights tab.
              </p>
            </div>
          ) : (
            <>
              {/* Verdict */}
              {(cat?.summary_points?.length || cat?.justification) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                    Verdict
                  </div>
                  <PointsOrProse points={cat?.summary_points} prose={cat?.justification} />
                </div>
              )}

              {/* Evidence — collapsible */}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEvidenceOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Evidence ({evidence.length})
                  </span>
                  <Chevron open={evidenceOpen} />
                </button>
                {evidenceOpen && (
                  <div className="p-4 space-y-2 bg-white">
                    {evidence.length > 0 ? (
                      evidence.map((ev, i) => (
                        <EvidenceItem key={i} ev={ev} onNavigateToTimestamp={jump} />
                      ))
                    ) : (
                      <p className="text-gray-400 text-xs">No evidence captured for this category.</p>
                    )}
                  </div>
                )}
              </div>

              {/* What went well (with its evidence) / The fix (with the words
                  to use instead) — two columns. Each side falls back to its
                  paragraph for calls graded before bullets existed. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="rounded-lg border border-green-200 bg-green-50/60 p-4">
                  <div className="flex items-center gap-2 text-green-700 font-semibold text-sm mb-2">
                    <span aria-hidden>&#10003;</span> What went well
                  </div>
                  {strengths.length > 0 ? (
                    <ul className="space-y-3">
                      {strengths.map((strength, i) => (
                        <StrengthItem
                          key={i}
                          strength={strength}
                          onNavigateToTimestamp={jump}
                        />
                      ))}
                    </ul>
                  ) : cat?.what_went_well ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{cat.what_went_well}</p>
                  ) : (
                    <span className="text-sm text-gray-400">&mdash;</span>
                  )}
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                  <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
                    <span aria-hidden>&#9998;</span> The fix
                  </div>
                  {fixes.length > 0 ? (
                    <ul className="space-y-3">
                      {fixes.map((fix, i) => (
                        <FixItem key={i} fix={fix} />
                      ))}
                    </ul>
                  ) : cat?.what_was_missed ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{cat.what_was_missed}</p>
                  ) : (
                    <span className="text-sm text-gray-400">&mdash;</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const RubricSection: React.FC<Props> = ({
  rubricAnalysis,
  onNavigateToTimestamp,
  isAdmin,
  onReanalyze,
  isReanalyzing,
  onContactSupport,
  multipleSalespeople,
  onAdjustWeights,
}) => {
  const categories = rubricAnalysis?.categories ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  if (!rubricAnalysis) {
    return (
      <div className="py-12 text-center text-gray-400 text-sm">
        No rubric analysis available for this call.
      </div>
    );
  }

  // A dimension "failed" when it has no score (raw_score null) or carries an
  // error. When any failed, the overall band/percentage below was computed from
  // only the graded dimensions, so it is unreliable — surface a top error state.
  const failedCategories = categories.filter((c) => c?.raw_score == null || !!c?.error);
  const gradedCount = categories.length - failedCategories.length;
  const hasFailures = failedCategories.length > 0;

  const bandKey = rubricAnalysis?.band ?? 'lost';
  const band = BAND_STYLE[bandKey] ?? { text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-l-gray-400' };
  const pct = rubricAnalysis?.percentage != null ? Math.round(rubricAnalysis.percentage * 100) : null;
  const narrative = rubricAnalysis?.narrative ?? {};

  // Weighting provenance. `isCustomWeighted` is computed by the analyzer from
  // the weights it actually graded with, so it stays correct for calls scored
  // before this field existed (undefined -> equal weights -> no badge).
  const isCustomWeighted = !!rubricAnalysis?.isCustomWeighted;
  const weightingInfo = rubricAnalysis?.weighting;
  const equalPct =
    rubricAnalysis?.equal_weight_percentage != null
      ? Math.round(rubricAnalysis.equal_weight_percentage * 100)
      : null;
  const hasScoringExplanation = !!(
    narrative?.weighting_rationale || narrative?.scoring_approach
  );
  const canAdjustWeights = !!(isAdmin && onAdjustWeights);

  const keyOf = (cat: RubricCategoryResult, i: number) => cat?.key ?? String(cat?.number ?? i);
  const selectedCat = selectedKey
    ? categories.find((c, i) => keyOf(c, i) === selectedKey) ?? null
    : null;

  return (
    <div className="space-y-5">
      {/* Incomplete-analysis error state — shown at the very top when any
          dimension failed to grade, so a partial/misleading overall score is
          never presented as final. */}
      {hasFailures && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 shadow-1">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl leading-none mt-0.5" aria-hidden>
              &#9888;
            </span>
            <div className="flex-1">
              <div className="text-sm font-bold text-red-800">
                Incomplete analysis — {failedCategories.length} of {categories.length}{' '}
                dimension{failedCategories.length === 1 ? '' : 's'} could not be graded
              </div>
              <p className="text-sm text-red-700 mt-1">
                The overall score below was computed from only the {gradedCount} graded
                dimension{gradedCount === 1 ? '' : 's'}, so it may not reflect the full call.
                Re-run the analysis, or open a support ticket if it keeps failing.
              </p>
              <p className="text-xs text-red-600 mt-1">
                Affected: {failedCategories.map((c, i) => c?.title ?? c?.key ?? `#${i + 1}`).join(', ')}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {isAdmin && onReanalyze && (
                  <button
                    type="button"
                    onClick={onReanalyze}
                    disabled={isReanalyzing}
                    className={BTN_PRIMARY}
                  >
                    {isReanalyzing ? 'Re-running…' : 'Re-run analysis'}
                  </button>
                )}
                {onContactSupport && (
                  <button type="button" onClick={onContactSupport} className={BTN_SECONDARY}>
                    Contact support
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Score summary */}
      <div className={`rounded-lg border border-gray-200 bg-white shadow-1 border-l-4 ${band.border} p-5`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2 flex-wrap">
              Discovery Call Rubric
              {multipleSalespeople && (
                <span className="text-[11px] font-bold uppercase tracking-wider text-purple-700 bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5">
                  Team score · 2 salespeople
                </span>
              )}
              {isCustomWeighted && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider text-obsidian bg-white border border-gray-300 rounded-full px-2 py-0.5 shadow-1"
                  title={
                    weightingInfo?.adjustedByName
                      ? `Weights adjusted by ${weightingInfo.adjustedByName}${
                          weightingInfo.adjustedAt
                            ? ` on ${formatCallInsightsTimestamp(weightingInfo.adjustedAt)}`
                            : ''
                        }`
                      : 'Graded with manager-set weights rather than equal weighting'
                  }
                >
                  Custom weights
                </span>
              )}
            </div>
            <div className={`text-lg font-bold mt-1 ${band.text} flex items-center gap-2`}>
              {rubricAnalysis?.band_label ?? '—'}
              {hasFailures && (
                <span className="text-[11px] font-bold uppercase tracking-wider text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                  Partial
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-800">
              {rubricAnalysis?.weighted_total ?? '–'}
              <span className="text-lg text-gray-400"> / {rubricAnalysis?.weighted_max ?? '–'}</span>
            </div>
            {pct != null && <div className="text-sm text-gray-500">{pct}%</div>}
          </div>
        </div>
        {rubricAnalysis?.band_interpretation && (
          <p className="text-sm text-gray-600 mt-2">{rubricAnalysis.band_interpretation}</p>
        )}
        {/* The equal-weight comparison lives in the "How this call was scored"
            panel below, next to the rationale that explains it. */}
        <p className="text-xs text-dim mt-2">
          {rubricAnalysis?.scored_count ?? categories.length}/
          {rubricAnalysis?.category_count ?? 10} categories graded
        </p>
      </div>

      {/* How the score was arrived at, and where the weights are tuned.
          Rendered whenever there is either an explanation to show OR an admin
          who can adjust the weighting — an older session graded before these
          fields existed still needs to expose the control. Kept above the
          coach's read because it frames how to read everything below it. */}
      {(hasScoringExplanation || canAdjustWeights) && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-1 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2.5">
              <span className="h-3.5 w-[3px] rounded-sm bg-plasma-cyan" aria-hidden />
              <span className={SECTION_LABEL}>How this call was scored</span>
            </div>
            {canAdjustWeights && (
              <button type="button" onClick={onAdjustWeights} disabled={isReanalyzing} className={BTN_PRIMARY}>
                Adjust weights
              </button>
            )}
          </div>

          <div className="px-5 py-4 space-y-4">
            {hasScoringExplanation ? (
              <>
                {narrative?.weighting_rationale && (
                  <div>
                    {/* At equal weights there is no weighting decision to
                        report — the section names what moved the total instead. */}
                    <div className={SUB_LABEL}>
                      {isCustomWeighted ? 'Weighting rationale' : 'What drove the score'}
                    </div>
                    <p className={BODY_TEXT}>{narrative.weighting_rationale}</p>
                  </div>
                )}
                {narrative?.scoring_approach && (
                  <div>
                    <div className={SUB_LABEL}>Scoring approach</div>
                    <p className={BODY_TEXT}>{narrative.scoring_approach}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-dim leading-relaxed">
                This call was graded before scoring explanations were added. Adjust the
                weights, or re-run the analysis, to generate them.
              </p>
            )}

            {/* Weighting provenance — the last line, so the numbers sit
                directly above the score sheet they explain. */}
            <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 border-t border-gray-100 pt-3 text-xs text-dim">
              {isCustomWeighted ? (
                <>
                  <span className="font-semibold text-obsidian">Custom weights</span>
                  {weightingInfo?.adjustedByName && (
                    <span>
                      · set by {weightingInfo.adjustedByName}
                      {weightingInfo.adjustedAt
                        ? ` on ${formatCallInsightsTimestamp(weightingInfo.adjustedAt)}`
                        : ''}
                    </span>
                  )}
                  {/* A reweighted score only means something next to the
                      unweighted one — without this, cross-rep comparison
                      silently drifts. */}
                  {equalPct != null && (
                    <span>
                      · {equalPct}% at equal weights
                      {rubricAnalysis?.equal_weight_band_label
                        ? ` (${rubricAnalysis.equal_weight_band_label})`
                        : ''}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-semibold text-obsidian">Equal weights</span>
                  <span>· all ten dimensions count the same on this call</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Coach's read (narrative) */}
      {(narrative?.highest_leverage_strength ||
        narrative?.highest_leverage_fix ||
        narrative?.where_the_deal_stands) && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-1 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <span className={SECTION_LABEL}>Coach&rsquo;s read</span>
          </div>
          <div className="px-5 py-4 space-y-4">
            {/* Strength and fix keep their semantic green/amber; the neutral
                third section takes the plasma-cyan accent rather than a blue
                that sits outside the palette. */}
            {narrative?.highest_leverage_strength && (
              <div className="border-l-2 border-band-strong pl-3">
                <div className={SUB_LABEL}>Highest-leverage strength</div>
                <p className={BODY_TEXT}>{narrative.highest_leverage_strength}</p>
              </div>
            )}
            {narrative?.highest_leverage_fix && (
              <div className="border-l-2 border-band-soft pl-3">
                <div className={SUB_LABEL}>Highest-leverage fix</div>
                <p className={BODY_TEXT}>{narrative.highest_leverage_fix}</p>
              </div>
            )}
            {narrative?.where_the_deal_stands && (
              <div className="border-l-2 border-plasma-cyan pl-3">
                <div className={SUB_LABEL}>Where the deal stands</div>
                <p className={BODY_TEXT}>{narrative.where_the_deal_stands}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Score sheet — card grid (all dimensions visible at once) */}
      <div className="space-y-3">
        {/* "Adjust weights" lives in the scoring panel above, beside the
            rationale it changes. */}
        <div className="flex items-center justify-between gap-3">
          <div className={SECTION_LABEL}>Score sheet</div>
          <span className="text-[11px] text-dim">Click a dimension for details</span>
        </div>
        {categories.length === 0 ? (
          <p className="text-sm text-gray-400">No categories were scored.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {categories.map((cat, i) => {
              const k = keyOf(cat, i);
              return (
                <CategoryCard
                  key={k}
                  cat={cat}
                  showWeight={isCustomWeighted}
                  onClick={() => setSelectedKey(k)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedCat && (
        <CategoryModal
          cat={selectedCat}
          onClose={() => setSelectedKey(null)}
          onNavigateToTimestamp={onNavigateToTimestamp}
        />
      )}
    </div>
  );
};

export default RubricSection;
